import React, { useState } from "react";
import { Helmet } from "react-helmet";
import styled from "styled-components";
import EmbeddedClinicList from "../../components/EmbeddedClinicList";
import {
  TwoColRow,
  ColText,
  ColImage,
  ColImg,
} from "../../components/ContentLayouts";

// ── Image imports ───────────────────────────────────────────────────────────────
import imgUltrasoundLocate from "../../assets/images/content/pet-ultrasound/ultrasound-locate.png";
import imgUltrasoundExpect from "../../assets/images/content/pet-ultrasound/ultrasound-expect.png";

const faqs = [
  {
    question: "Why does my pet need an ultrasound scan?",
    answer:
      "Vets recommend a pet ultrasound to get a clear, closer look at organs and soft tissue that an X-ray can't show well, helping them diagnose issues like digestive problems, organ concerns, or confirm a pregnancy.",
  },
  {
    question: "Is ultrasound safe for pets?",
    answer:
      "Yes. It uses sound waves, not radiation, so it's safe even if your pet needs repeat scans over time.",
  },
  {
    question: "What can an ultrasound detect in pets?",
    answer:
      "It can show the condition of organs such as the liver, kidneys, bladder, and intestines, along with fluid buildup, growths, or an early pregnancy.",
  },
  {
    question: "Does my pet need special preparation before an ultrasound?",
    answer:
      "Sometimes. Your vet may ask for a short fasting period before an abdominal scan, and a small area of fur may be trimmed for clearer images. Your vet will guide you based on the scan type.",
  },
  {
    question: "How long does a pet ultrasound take?",
    answer:
      "Most scans take between 15 and 30 minutes, depending on which area is being examined and how cooperative your pet is during the visit.",
  },
  {
    question: "Will my pet need sedation for the scan?",
    answer:
      "Most pets don't need sedation. It's only considered for anxious or restless pets, or when a guided procedure is done alongside the scan.",
  },
  {
    question: "How much does a pet ultrasound cost in Bangalore?",
    answer:
      "Cost depends on the scan type and clinic, along with any add-ons like a specialist review or sedation. PetQura shows clinic-specific details when you search, so you can compare before booking.",
  },
  {
    question: "How do I book a pet ultrasound near me through PetQura?",
    answer:
      "Just enter your location and select the ultrasound service on PetQura. We'll show you nearby clinics so you can compare and book an appointment in a few steps.",
  },
];

const FaqItem = ({ question, answer }) => {
  const [open, setOpen] = useState(false);
  return (
    <FaqRow>
      <FaqQuestion onClick={() => setOpen(!open)}>
        <span>{question}</span>
        <FaqIcon>{open ? "−" : "+"}</FaqIcon>
      </FaqQuestion>
      {open && <FaqAnswer>{answer}</FaqAnswer>}
    </FaqRow>
  );
};

const PetUltrasound = () => {
  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Pet Ultrasound Services in Bangalore | PetQura</title>
        <meta
          name="description"
          content="Looking for pet ultrasound near you? Find verified veterinary clinics offering pet ultrasound scans in Bangalore and book a diagnostic appointment in just a few clicks."
        />
        <link
          rel="canonical"
          href="https://petqura.com/services/pet-ultrasound-bangalore"
        />
      </Helmet>

      <PageWrapper>
        <HeroSection>
          <h1>Pet Ultrasound Clinics Near You in Bangalore by PetQura</h1>
          <EmbeddedClinicList serviceId={null} previewMode />
          <TwoColRow>
            <ColText>
              <p>
                Bengaluru pet parents looking for a{" "}
                <em>pet ultrasound near me</em> now have an easier way to
                find the right clinic. At PetQura, we connect you with
                veterinary clinics across Bangalore that offer this service,
                based on your pet's exact needs. Unlike an X-ray, it doesn't
                use radiation, so vets are comfortable recommending it more
                than once if needed.
              </p>
              <p>
                Through PetQura, you simply look up a pet ultrasound near
                you, compare clinics close to your location, and schedule a
                visit in a few taps, less running around for you and your
                pet.
              </p>
            </ColText>
            <ColImage>
              <ColImg
                src={imgUltrasoundLocate}
                alt="Finding a pet ultrasound clinic near you in Bangalore"
              />
            </ColImage>
          </TwoColRow>
        </HeroSection>

        {/* ── When Needed ── */}
        <ContentSection>
          <SectionHeading>
            When Does Your Pet Need an Ultrasound Scan?
          </SectionHeading>
          <p>
            Vets usually suggest a pet ultrasound when they need a closer look
            at what's happening inside your pet's body. A few common reasons
            this scan gets recommended:
          </p>
          <BulletList>
            <li>Ongoing vomiting, diarrhoea, or loss of appetite</li>
            <li>Suspected bladder stones or difficulty urinating</li>
            <li>Confirming and tracking a pregnancy</li>
            <li>
              Checking the size and condition of the liver, kidneys, or spleen
            </li>
            <li>Investigating a lump, swelling, or unexplained growth</li>
            <li>Following up on something flagged in earlier blood work</li>
          </BulletList>
          <p>
            If your pet is showing any of these signs, we can help you find a
            clinic near you that fits your schedule.
          </p>
        </ContentSection>

        {/* ── What to Expect ── */}
        <ContentSection>
          <SectionHeading>
            What to Expect During a Pet Ultrasound
          </SectionHeading>
          <TwoColRow>
            <ColText>
              <p>
                It's painless and non-invasive, so there's no need to worry
                before the appointment. The vet applies a gel to the area
                being examined and gently glides a small handheld probe over
                the skin. This sends real-time images to a monitor, letting
                the vet study the organ from a few different angles for an
                accurate read. Most pets stay awake and calm through the
                process, sedation is only used in rare cases, such as when a
                pet is anxious or a guided procedure is involved. Depending
                on the area being scanned, the visit usually takes 15 to 30
                minutes, and you can often stay in the room with your pet the
                whole time.
              </p>
            </ColText>
            <ColImage>
              <ColImg
                src={imgUltrasoundExpect}
                alt="What to expect during a pet ultrasound"
              />
            </ColImage>
          </TwoColRow>
        </ContentSection>

        {/* ── Cost ── */}
        <ContentSection>
          <SectionHeading>Cost of Pet Ultrasound in Bangalore</SectionHeading>
          <p>
            The cost of a pet ultrasound in Bangalore depends on a few
            factors: the organ or area being scanned, whether the case needs a
            specialist's review, and whether any additional step, like fluid
            sampling, is done during the same visit. A basic, single-region
            scan usually costs less than a detailed abdominal one, and a
            pregnancy scan is often priced separately from a diagnostic scan.
            Sedation, pre-sedation blood work, and after-hours visits can add
            to the final bill too. Pricing varies from clinic to clinic, so we
            show you pet ultrasound Bangalore pricing upfront on PetQura, no
            surprises at the counter.
          </p>
        </ContentSection>

        {/* ── Finding Near You ── */}
        <ContentSection>
          <SectionHeading>
            Finding a Pet Ultrasound Near You in Bangalore
          </SectionHeading>
          <p>
            Finding the right vet shouldn't take hours of calling around. On
            PetQura, you enter your location and the service you're looking
            for, and we show you veterinary clinics near you across Bangalore
            that offer this scan and other details to help you decide. This
            way, you can book an appointment quickly, without the stress of
            comparing clinics one by one, especially useful when your pet is
            unwell and time matters. Whether you're in Indiranagar, Whitefield,
            HSR Layout, or anywhere else in the city, PetQura helps you locate
            an option that's actually close to home.
          </p>
        </ContentSection>

        {/* ── FAQ ── */}
        <ContentSection>
          <SectionHeading>Frequently Asked Questions (FAQs)</SectionHeading>
          <FaqList>
            {faqs.map((faq, idx) => (
              <FaqItem key={idx} question={faq.question} answer={faq.answer} />
            ))}
          </FaqList>
        </ContentSection>
      </PageWrapper>
    </>
  );
};

export default PetUltrasound;

// ─── Styled Components ────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 3rem 5% 5rem;

  @media (max-width: 768px) {
    padding: 2rem 5% 4rem;
  }
`;

const HeroSection = styled.section`
  margin-bottom: 3rem;

  h1 {
    font-size: 2rem;
    font-weight: 700;
    color: #1a1a2e;
    margin-bottom: 1.5rem;
    font-family: Montserrat, sans-serif;
    text-align: center;

    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }

  p {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-top: 1.5rem;
    margin-bottom: 1rem;
  }
`;

const ContentSection = styled.section`
  margin-bottom: 2.5rem;

  p {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-bottom: 1rem;
  }
`;

const SectionHeading = styled.h2`
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 1rem;
  font-family: Montserrat, sans-serif;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const BulletList = styled.ul`
  padding-left: 1.4rem;
  margin-bottom: 1rem;

  li {
    font-size: 16px;
    line-height: 1.8;
    color: #3a3a3a;
    font-family: Montserrat, sans-serif;
    margin-bottom: 0.6rem;
  }
`;

const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
`;

const FaqRow = styled.div`
  border-bottom: 1px solid #e5e5e5;

  &:last-child {
    border-bottom: none;
  }
`;

const FaqQuestion = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #ffffff;
  border: none;
  padding: 1rem 1.25rem;
  text-align: left;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  font-family: Montserrat, sans-serif;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: #f7f9ff;
  }
`;

const FaqIcon = styled.span`
  font-size: 20px;
  font-weight: 400;
  color: #00419d;
  flex-shrink: 0;
  margin-left: 1rem;
`;

const FaqAnswer = styled.div`
  padding: 0 1.25rem 1rem;
  font-size: 15px;
  line-height: 1.75;
  color: #555555;
  font-family: Montserrat, sans-serif;
  background: #fafafa;
`;
